import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Modal } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { useSearch } from '../hooks/useSearch';
import { useFilters } from '../hooks/useFilters';
import { usePagination } from '../hooks/usePagination';
import { ColumnCustomizer } from '../components/ColumnCustomizer';
import { Tooltip } from '../components/ui/Tooltip';
import { ActivityDetailModal } from '../components/operations/ActivityDetailModal';
import { EditActivityModal } from '../components/operations/EditActivityModal';
import { GenerateActivityWizardModal } from '../components/operations/GenerateActivityWizardModal';
import { operativeActivitiesStore, getProgress, getCurrentStepName, OperativeActivity } from '../services/operativeActivities';
import { Plus, Filter, Download, Eye, Edit, Search, X, Trash2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useNotifications } from '../contexts/NotificationContext';
import { motion, AnimatePresence } from 'framer-motion';
interface TableRow {
  id: string;
  type: 'standard' | 'personalizzata';
  code: string;
  client: string;
  contract: string;
  product: string;
  operator: string;
  stepName: string;
  percentage: number;
  isCompleted: boolean;
  deadline: string;
  rawActivity: OperativeActivity;
}
function formatDateIT(isoDate: string): string {
  try {
    const d = new Date(isoDate);
    return d.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch {
    return isoDate;
  }
}
function buildTableRows(): TableRow[] {
  const activities = operativeActivitiesStore.getAll();
  return activities.map((act, idx) => {
    const progress = getProgress(act);
    const stepName = getCurrentStepName(act);
    return {
      id: act.id,
      type: act.activityType,
      code: `ATT-2025/${String(idx + 1).padStart(3, '0')}`,
      client: act.clientName,
      contract: act.contractNumber,
      product: act.product,
      operator: act.operator,
      stepName,
      percentage: progress.percentage,
      isCompleted: progress.isCompleted,
      deadline: formatDateIT(act.deadline),
      rawActivity: act
    };
  });
}
const columnsConfig = [{
  key: 'tipo',
  label: 'Tipo'
}, {
  key: 'code',
  label: 'Codice'
}, {
  key: 'client',
  label: 'Cliente'
}, {
  key: 'contract',
  label: 'Contratto'
}, {
  key: 'product',
  label: 'Prodotto'
}, {
  key: 'operator',
  label: 'Operatore'
}, {
  key: 'avanzamento',
  label: 'Avanzamento'
}, {
  key: 'deadline',
  label: 'Scadenza'
}, {
  key: 'actions',
  label: 'Azioni'
}];
export function OperationsPage() {
  const location = useLocation();
  const [selectedActivity, setSelectedActivity] = useState<OperativeActivity | null>(null);
  const [isActivityDetailOpen, setIsActivityDetailOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isGenerateActivityOpen, setIsGenerateActivityOpen] = useState(false);
  const [editActivityForWizard, setEditActivityForWizard] = useState<OperativeActivity | null>(null);
  const [wizardContractNumber, setWizardContractNumber] = useState<string | undefined>();
  const [wizardClientName, setWizardClientName] = useState<string | undefined>();
  const [showFilters, setShowFilters] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(columnsConfig.map(c => c.key));
  const [viewMode, setViewMode] = useState<'aperte' | 'completate'>('aperte');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<TableRow | null>(null);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [, setRefreshKey] = useState(0);
  const {
    addNotification
  } = useNotifications();
  const allRows = useMemo(() => buildTableRows(), []);
  const baseData = useMemo(() => viewMode === 'aperte' ? allRows.filter(r => !r.isCompleted) : allRows.filter(r => r.isCompleted), [viewMode, allRows]);
  const {
    filters,
    setFilter,
    clearFilters,
    filteredData: filterResult
  } = useFilters(baseData);
  const {
    searchTerm,
    setSearchTerm,
    filteredData: searchResult
  } = useSearch(filterResult, ['code', 'client', 'contract', 'product', 'operator']);
  const {
    currentPage,
    totalPages,
    paginatedData,
    goToPage,
    pageSize,
    setPageSize,
    totalItems,
    setCurrentPage
  } = usePagination(searchResult);
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm, viewMode]);
  // Handle navigation from ContractsPage to open wizard
  useEffect(() => {
    const state = location.state as any;
    if (state?.openWizard) {
      setWizardContractNumber(state.contractNumber);
      setWizardClientName(state.clientName);
      setIsGenerateActivityOpen(true);
      // Clear the state so it doesn't re-open on re-render
      window.history.replaceState({}, '');
    }
  }, [location.state]);
  const handleView = (row: TableRow) => {
    setSelectedActivity(row.rawActivity);
    setIsActivityDetailOpen(true);
  };
  const handleEdit = (row: TableRow) => {
    setEditActivityForWizard(row.rawActivity);
    setIsGenerateActivityOpen(true);
  };
  const handleDelete = (row: TableRow) => {
    setItemToDelete(row);
    setShowDeleteDialog(true);
  };
  const confirmDelete = () => {
    setDeleteSuccess(true);
    setTimeout(() => {
      setDeleteSuccess(false);
      setShowDeleteDialog(false);
      setItemToDelete(null);
      addNotification('success', 'Eliminato', 'Attività eliminata con successo');
    }, 1500);
  };
  const handleActivityStepComplete = (activityId: string, stepIndex: number) => {
    operativeActivitiesStore.completeStep(activityId, stepIndex);
    const updated = operativeActivitiesStore.getById(activityId);
    if (updated) setSelectedActivity({
      ...updated
    });
    setRefreshKey(r => r + 1);
  };
  const handleEditFromDetail = (activity: OperativeActivity) => {
    setIsActivityDetailOpen(false);
    setTimeout(() => {
      setEditActivityForWizard(activity);
      setIsGenerateActivityOpen(true);
    }, 200);
  };
  const hasActiveFilters = Object.keys(filters).some(k => filters[k]);
  return <Layout data-id="element-4639">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4" data-id="element-4640">
        <div data-id="element-4641">
          <h1 className="text-3xl font-bold text-seform-navy" data-id="element-4642">
            Attività operative
          </h1>
          <p className="text-slate-500 mt-1" data-id="element-4643">
            Pianificazione e monitoraggio delle attività operative.
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-4644">
          <div className="bg-slate-100 p-1 rounded-xl flex items-center" data-id="element-4645">
            <button onClick={() => setViewMode('aperte')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${viewMode === 'aperte' ? 'bg-white text-seform-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-4646">
              Aperte
            </button>
            <button onClick={() => setViewMode('completate')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${viewMode === 'completate' ? 'bg-white text-seform-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-4647">
              Completate
            </button>
          </div>
          <Button variant="flat" color="secondary" startContent={<Download className="h-4 w-4" data-id="element-4649" />} data-id="element-4648">
            Esporta
          </Button>
          <Button startContent={<Plus className="h-4 w-4" data-id="element-4651" />} onClick={() => {
          setWizardContractNumber(undefined);
          setWizardClientName(undefined);
          setIsGenerateActivityOpen(true);
        }} data-id="element-4650">
            Nuova attività
          </Button>
        </div>
      </div>

      <Card className="mb-6" noPadding data-id="element-4652">
        <div className="p-4 flex flex-col gap-4" data-id="element-4653">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-4654">
            <div className="flex-1" data-id="element-4655">
              <Input placeholder="Cerca codice, cliente, contratto, prodotto, operatore..." startContent={<Search className="h-4 w-4" data-id="element-4657" />} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} fullWidth data-id="element-4656" />
            </div>
            <div className="flex gap-2" data-id="element-4658">
              <Button variant={showFilters ? 'solid' : 'flat'} color="secondary" onClick={() => setShowFilters(!showFilters)} startContent={<Filter className="h-4 w-4" data-id="element-4660" />} data-id="element-4659">
                Filtri{' '}
                {hasActiveFilters && <Badge variant="primary" size="sm" className="ml-1" data-id="element-4661">
                    {Object.keys(filters).filter(k => filters[k]).length}
                  </Badge>}
              </Button>
              <ColumnCustomizer columns={columnsConfig} visibleColumns={visibleColumns} onColumnChange={setVisibleColumns} pageKey="operations" lockedColumns={['code']} data-id="element-4662" />
            </div>
          </div>

          {showFilters && <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 pt-4 border-t border-slate-100 animate-in fade-in slide-in-from-top-2" data-id="element-4663">
              <Select label="Tipo" value={filters.type || ''} onChange={e => setFilter('type', e.target.value)} options={[{
            value: '',
            label: 'Tutti'
          }, {
            value: 'standard',
            label: 'Standard'
          }, {
            value: 'personalizzata',
            label: 'Personalizzata'
          }]} data-id="element-4664" />
              <Select label="Operatore" value={filters.operator || ''} onChange={e => setFilter('operator', e.target.value)} options={[{
            value: '',
            label: 'Tutti'
          }, {
            value: 'Alessio',
            label: 'Alessio'
          }, {
            value: 'Lorenzo',
            label: 'Lorenzo'
          }, {
            value: 'Gian Franco',
            label: 'Gian Franco'
          }, {
            value: 'Tiziano',
            label: 'Tiziano'
          }]} data-id="element-4665" />
              <div className="flex items-end" data-id="element-4666">
                <Button variant="flat" color="danger" size="sm" onClick={clearFilters} startContent={<X className="h-4 w-4" data-id="element-4668" />} data-id="element-4667">
                  Resetta filtri
                </Button>
              </div>
            </div>}
        </div>
      </Card>

      <Card noPadding className="overflow-hidden relative" data-id="element-4669">
        <div className="overflow-x-auto" data-id="element-4670">
          <table className="w-full text-left border-collapse table-auto" data-id="element-4671">
            <thead className="sticky top-0 z-10 bg-slate-50 border-b border-slate-200 shadow-sm" data-id="element-4672">
              <tr className="text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-4673">
                {columnsConfig.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className={`px-3 py-3 whitespace-nowrap ${col.key === 'actions' ? 'w-28' : ''} ${col.key === 'avanzamento' ? 'min-w-[220px]' : ''}`} data-id="element-4674">
                      {col.label}
                    </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-4675">
              {paginatedData.map(row => <tr key={row.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-4676">
                  {visibleColumns.includes('tipo') && <td className="px-3 py-3 whitespace-nowrap" data-id="element-4677">
                      <Badge variant={row.type === 'standard' ? 'info' : 'warning'} size="sm" data-id="element-4678">
                        {row.type === 'standard' ? 'Standard' : 'Personalizzata'}
                      </Badge>
                    </td>}
                  {visibleColumns.includes('code') && <td className="px-3 py-3 font-mono text-xs text-slate-500 whitespace-nowrap" data-id="element-4679">
                      {row.code}
                    </td>}
                  {visibleColumns.includes('client') && <td className="px-3 py-3 text-sm text-slate-700 truncate max-w-[160px]" title={row.client} data-id="element-4680">
                      {row.client}
                    </td>}
                  {visibleColumns.includes('contract') && <td className="px-3 py-3 font-mono text-xs text-slate-500 whitespace-nowrap" data-id="element-4681">
                      {row.contract}
                    </td>}
                  {visibleColumns.includes('product') && <td className="px-3 py-3 text-sm text-slate-600 truncate max-w-[140px]" title={row.product} data-id="element-4682">
                      {row.product}
                    </td>}
                  {visibleColumns.includes('operator') && <td className="px-3 py-3 text-sm text-slate-600 whitespace-nowrap" data-id="element-4683">
                      {row.operator}
                    </td>}
                  {visibleColumns.includes('avanzamento') && <td className="px-3 py-3" data-id="element-4684">
                      <div className="flex items-center justify-between gap-3" data-id="element-4685">
                        <span className={`text-sm truncate ${row.isCompleted ? 'text-green-700 font-semibold' : 'text-slate-700'}`} title={row.stepName} data-id="element-4686">
                          {row.stepName}
                        </span>
                        <span className={`flex-shrink-0 text-xs font-bold px-2 py-0.5 rounded-md border ${row.percentage === 100 ? 'bg-green-50 text-green-700 border-green-200' : row.percentage === 0 ? 'bg-slate-50 text-slate-400 border-slate-200' : 'bg-orange-50 text-seform-orange border-orange-200'}`} data-id="element-4687">
                          {row.percentage}%
                        </span>
                      </div>
                    </td>}
                  {visibleColumns.includes('deadline') && <td className="px-3 py-3 text-sm text-slate-600 whitespace-nowrap" data-id="element-4688">
                      {row.deadline}
                    </td>}
                  {visibleColumns.includes('actions') && <td className="px-3 py-3 w-28" data-id="element-4689">
                      <div className="flex items-center gap-1" data-id="element-4690">
                        <Tooltip content="Visualizza attività" data-id="element-4691">
                          <Button variant="light" size="sm" className="!px-1" onClick={() => handleView(row)} data-id="element-4692">
                            <Eye className="h-4 w-4 text-slate-500" data-id="element-4693" />
                          </Button>
                        </Tooltip>
                        <Tooltip content="Modifica attività" data-id="element-4694">
                          <Button variant="light" size="sm" className="!px-1" onClick={() => handleEdit(row)} data-id="element-4695">
                            <Edit className="h-4 w-4 text-seform-orange" data-id="element-4696" />
                          </Button>
                        </Tooltip>
                        <Tooltip content="Elimina attività" data-id="element-4697">
                          <Button variant="light" size="sm" className="!px-1" onClick={() => handleDelete(row)} data-id="element-4698">
                            <Trash2 className="h-4 w-4 text-red-500" data-id="element-4699" />
                          </Button>
                        </Tooltip>
                      </div>
                    </td>}
                </tr>)}
              {paginatedData.length === 0 && <tr data-id="element-4700">
                  <td colSpan={visibleColumns.length} className="px-4 py-12 text-center text-slate-400 text-sm" data-id="element-4701">
                    Nessuna attività trovata.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
        <div className="border-t border-slate-100" data-id="element-4702">
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-4703" />
        </div>
      </Card>

      {/* Activity Detail Modal */}
      <ActivityDetailModal isOpen={isActivityDetailOpen} onClose={() => setIsActivityDetailOpen(false)} activity={selectedActivity} onStepComplete={handleActivityStepComplete} onEdit={handleEditFromDetail} data-id="element-4704" />

      {/* Edit Activity Modal */}
      <EditActivityModal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} activity={selectedActivity} onSave={() => {
      setRefreshKey(r => r + 1);
    }} data-id="element-4705" />

      {/* Generate Activity Wizard Modal */}
      <GenerateActivityWizardModal isOpen={isGenerateActivityOpen} onClose={() => {
      setIsGenerateActivityOpen(false);
      setTimeout(() => setEditActivityForWizard(null), 300);
    }} contractNumber={wizardContractNumber} clientName={wizardClientName} editActivity={editActivityForWizard || undefined} data-id="element-4706" />

      {/* Delete Confirmation Modal */}
      <Modal isOpen={showDeleteDialog} onClose={() => setShowDeleteDialog(false)} title="Conferma eliminazione" size="sm" data-id="element-4707">
        <AnimatePresence mode="wait" data-id="element-4708">
          {deleteSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.3
        }} className="flex flex-col items-center justify-center py-12" data-id="element-4709">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 150,
            damping: 15,
            delay: 0.1
          }} className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-4710">
                <CheckCircle2 className="w-10 h-10 text-green-500" data-id="element-4711" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-4712">
                Eliminato con successo
              </p>
            </motion.div> : <motion.div key="confirm" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-4713">
              <div className="space-y-6" data-id="element-4714">
                <div className="bg-red-50 p-4 rounded-lg border border-red-100 flex gap-3" data-id="element-4715">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" data-id="element-4716" />
                  <div data-id="element-4717">
                    <p className="text-sm text-red-700 font-medium" data-id="element-4718">
                      Attenzione: questa azione è irreversibile.
                    </p>
                    <p className="text-sm text-red-600 mt-1" data-id="element-4719">
                      L'attività <strong data-id="element-4720">{itemToDelete?.code}</strong> verrà
                      eliminata definitivamente.
                    </p>
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4" data-id="element-4721">
                  <Button variant="flat" color="default" onClick={() => setShowDeleteDialog(false)} data-id="element-4722">
                    Annulla
                  </Button>
                  <Button color="danger" onClick={confirmDelete} startContent={<Trash2 className="h-4 w-4" data-id="element-4724" />} data-id="element-4723">
                    Elimina
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>
    </Layout>;
}